import React, { useCallback, useContext } from 'react'
import { Box, Icon, Paragraph, Stack } from '@ui'
import { useStore } from 'store/store'
import { MotionBox } from 'ui/components/Motion/MotionComponents'
import { CardOpener } from 'ui/components/Overlay/CardOpener'
import { CardOpenerContext } from 'ui/components/Overlay/CardOpenerContext'

import { themeOptions } from './ThemeConstants'

type ThemeOption = (typeof themeOptions)[number]

export function ThemeSettingsDropdown() {
    const userTheme = useStore(({ userTheme }) => userTheme || 'dark')
    const setTheme = useStore(({ setTheme }) => setTheme)

    const currentOption =
        themeOptions.find((option) => option.value === userTheme) || themeOptions[0]

    const onChange = useCallback(
        (option: ThemeOption) => {
            setTheme(option.value)
        },
        [setTheme],
    )

    return (
        <CardOpener
            placement="dropdown"
            render={
                <Box paddingY="sm">
                    <MotionBox
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        background="level2"
                        borderRadius="sm"
                        boxShadow="dropdown"
                        overflow="hidden"
                        position="relative"
                    >
                        {themeOptions.map((option, index) => (
                            <ThemeOption
                                key={option.title}
                                option={option}
                                isSelected={option.value === userTheme}
                                paddingY="sm"
                                paddingTop={index === 0 ? 'md' : undefined}
                                paddingBottom={index === themeOptions.length - 1 ? 'md' : undefined}
                                onSelect={onChange}
                            />
                        ))}
                    </MotionBox>
                </Box>
            }
        >
            {({ triggerProps }) => (
                <Box horizontal gap="sm" alignItems="center" width="100%" {...triggerProps}>
                    <Box width="height_md" alignItems="center">
                        <Icon type={currentOption.icon} size="square_sm" />
                    </Box>
                    <Paragraph color="default">{currentOption.title}</Paragraph>
                </Box>
            )}
        </CardOpener>
    )
}

const ThemeOption = ({
    option,
    isSelected,
    onSelect,
    ...boxProps
}: {
    option: ThemeOption
    isSelected: boolean
    onSelect: (option: ThemeOption) => void
} & Omit<React.ComponentProps<typeof Box>, 'onSelect'>) => {
    const ctx = useContext(CardOpenerContext)

    return (
        <Box
            hoverable
            cursor="pointer"
            background="level2"
            paddingX="md"
            paddingY="sm"
            role="option"
            onClick={() => {
                onSelect(option)
                ctx?.closeCard()
            }}
            {...boxProps}
        >
            <Stack horizontal gap="sm">
                <Box
                    centerContent
                    width={{ desktop: 'x4', mobile: 'x3' }}
                    height={{ desktop: 'x4', mobile: 'x3' }}
                    rounded="xs"
                    background="level3"
                    shrink={false}
                >
                    <Icon type={option.icon} size="square_sm" color="gray2" />
                </Box>
                <Stack grow gap="sm" shrink={false} justifyContent="center">
                    <Paragraph color="default" size={{ desktop: 'md', mobile: 'sm' }}>
                        {option.title}
                    </Paragraph>
                    <Paragraph
                        color="gray2"
                        size={{ desktop: 'sm', mobile: 'xs' }}
                        whiteSpace={{ desktop: 'nowrap', mobile: 'normal' }}
                    >
                        {option.description}
                    </Paragraph>
                </Stack>
                <Stack centerContent width={{ default: 'x3', mobile: 'x1' }} shrink={false}>
                    {isSelected && (
                        <Icon
                            type="check"
                            size={{ desktop: 'square_sm', mobile: 'square_xs' }}
                            color="default"
                        />
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}
